import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import "./stars.css";

const GlassCard = ({ title, children }) => (
  <motion.div
    className="bg-white/20 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-8 my-6 w-full max-w-[1200px] mx-auto"
    whileHover={{ scale: 1.03 }}
  >
    <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
    <div className="text-white/80">{children}</div>
  </motion.div>
);

function App() {
  const [activeSection, setActiveSection] = useState("about-anchor");
  const [showTop, setShowTop] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 120;
      const sections = ["about-anchor", "contact-anchor", "skills-anchor", "projects-anchor", "services-anchor"];
      for (let id of sections) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= scrollY) {
          setActiveSection(id);
        }
      }
      setShowTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    const style = document.createElement("style");
    style.innerHTML = `a { text-decoration: none !important; }`;
    document.head.appendChild(style);
  }, []);

  const menuItems = [
    { id: "about-anchor", label: "About" },
    { id: "contact-anchor", label: "Contact" },
    { id: "skills-anchor", label: "Skills" },
    { id: "projects-anchor", label: "Projects" },
    { id: "services-anchor", label: "Services" }
  ];

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-sky-900 to-indigo-900 p-4 overflow-x-hidden">
      <div id="stars"></div>
      <div id="stars2"></div>
      <div id="stars3"></div>

      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur bg-black/30 text-white shadow-lg">
        <nav className="max-w-[1200px] mx-auto p-4 text-sm flex flex-wrap items-center justify-between">
          <a href="/" className="font-bold tracking-widest text-lg">Hao Ying</a>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden focus:outline-none text-white"
          >
            ☰
          </button>
          <ul className={`w-full md:w-auto md:flex gap-4 mt-4 md:mt-0 ${menuOpen ? "block" : "hidden md:flex"}`}>
            {menuItems.map(({ id, label }) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  className={`block md:inline hover:underline ${activeSection === id ? "font-bold underline" : ""}`}
                  onClick={() => setMenuOpen(false)}
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <main className="pt-24">
        <section className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center relative z-10">
          <motion.img
            src="/avatar.png"
            alt="Avatar"
            className="w-32 h-32 rounded-full mb-4 border-4 border-white/20 shadow-lg"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
          />
          <motion.h1
            className="text-4xl md:text-6xl font-extrabold text-white drop-shadow-2xl"
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Cheang Hao Ying
          </motion.h1>
          <p className="mt-4 text-lg md:text-xl text-white/80">Freelance Web Developer</p>
          <p className="text-white/70 mt-2">Johor Bahru, Malaysia</p>
          <div className="flex gap-4 mt-3">
            <a href="https://www.facebook.com/cheanghaoying" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white">
              <i className="fab fa-facebook fa-lg"></i>
            </a>
            <a href="https://instagram.com/c_haoying/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white">
              <i className="fab fa-instagram fa-lg"></i>
            </a>
            <a href="https://www.linkedin.com/in/hao-ying-cheang-3100761b1/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white">
              <i className="fab fa-linkedin fa-lg"></i>
            </a>
          </div>
        </section>

        <div id="about-anchor" className="h-24 -mt-24"></div>
        <div id="about">
          <GlassCard title="About Me">
            <p>
              I am a seasoned web developer with mastery in PHP, CSS, HTML5, MySQL, JavaScript, Bootstrap, WordPress, Joomla, C++, Java, jQuery, AJAX, E-Commerce and SEO. With over 5 years of experience, I have developed a deep understanding of web development principles and best practices. I am passionate about creating dynamic and user-friendly websites or web applications that meet and exceed client expectations.
            </p>
          </GlassCard>
        </div>

        <div id="contact-anchor" className="h-24 -mt-24"></div>
        <div id="contact">
          <GlassCard title="Contact">
            <p>Email: <a href="#" onClick={(e) => {e.preventDefault(); const name = "haoyingcheang"; const domain = "gmail.com"; window.location.href = `mailto:${name}@${domain}`;}}>haoyingcheang [at] gmail [dot] com</a></p>
            <p className="mt-2">More details available in my <a href="/hao_resume_2024.pdf" className="underline text-white/90">attached resume (PDF)</a>.</p>
          </GlassCard>
        </div>

        <div id="skills-anchor" className="h-24 -mt-24"></div>
        <div id="skills">
          <GlassCard title="Skills">
            <div className="flex gap-3 flex-wrap">
              {["PHP", "HTML5", "CSS", "JavaScript", "MySQL", "WordPress", "Joomla", "Yii2", "Laravel", "React"]
                .map((skill) => (
                  <span key={skill} className="px-4 py-2 bg-white/30 rounded-full text-white text-sm">
                    {skill}
                  </span>
                ))}
            </div>
          </GlassCard>
        </div>

        <div id="projects-anchor" className="h-24 -mt-24"></div>
        <div id="projects">
          <GlassCard title="Projects">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {[
                { url: "https://ace-my.com/", name: "ace-my.com", image: "/images/projects/ace-my.jpg" },
                { url: "https://boatnoodle.com.my/", name: "boatnoodle.com.my", image: "/images/projects/boatnoodle.jpg" },
                { url: "http://phhp.com.my/", name: "phhp.com.my", image: "/images/projects/phhp.jpg" },
                { url: "https://wshasia.com/", name: "wshasia.com", image: "/images/projects/wshasia.jpg" },
                { url: "https://sansmart.co/", name: "sansmart.co", image: "/images/projects/sansmart.jpg" },
                { url: "https://second-dimension.org/", name: "second-dimension.org", image: "/images/projects/second-dimension.jpg" },
              ].map((site, index) => (
                <a key={index} href={site.url} target="_blank" rel="noopener noreferrer" className="block bg-white/10 backdrop-blur rounded-lg p-4 hover:bg-white/20 transition shadow border border-white/10">
                  <div className="aspect-[16/9] rounded-md mb-3 overflow-hidden">
                    <img
                      src={site.image}
                      alt={site.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-100"
                    />
                  </div>
                  <p className="text-center text-white text-sm">{site.name}</p>
                </a>
              ))}
            </div>
          </GlassCard>
        </div>

        <div id="services-anchor" className="h-24 -mt-24"></div>
        <div id="services">
          <GlassCard title="Services">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  plan: "Basic",
                  price: "RM 900+",
                  features: ["1-3 Page Website", "Responsive Design", "Basic Contact Form", "Free SSL", "Delivery in 5 Days"]
                },
                {
                  plan: "Professional",
                  price: "RM 2,500+",
                  features: ["Up to 10 Pages", "CMS (WordPress)", "SEO Optimization", "Performance Tuning", "Delivery in 10 Days"]
                },
                {
                  plan: "Enterprise",
                  price: "RM 5,000+",
                  features: ["E-Commerce Integration", "Custom Plugin/Theme", "Payment Gateway Setup", "Maintenance & Support", "Delivery in 15+ Days"]
                }
              ].map((svc, index) => (
                <div key={index} className="bg-white/10 rounded-xl p-6 text-white border border-white/10 shadow-md text-center">
                  <h3 className="text-xl font-semibold mb-2">{svc.plan}</h3>
                  <p className="text-3xl font-bold mb-4">{svc.price}</p>
                  <ul className="text-sm space-y-2">
                    {svc.features.map((f, i) => <li key={i}>✓ {f}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>

        <div className="max-w-[1200px] mx-auto mt-10 mb-20">
          <h2 className="text-2xl font-bold text-white mb-4 text-center">Service Capabilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: "💡", title: "Web App Design", description: "Design intuitive and scalable user interfaces for SaaS, dashboards, or custom portals." },
              { icon: "🖥️", title: "Domain & Hosting", description: "Manage domain setup, secure shared/VPS hosting, and DNS configuration." },
              { icon: "🎯", title: "Digital Marketing", description: "Run FB/IG/Google Ads, tracking pixels, and analytics to drive sales or leads." },
              { icon: "📈", title: "SEO Optimization", description: "  Improve ranking with technical SEO, keyword targeting, and metadata enhancement." },
              { icon: "☁️", title: "Cloud Deployment", description: "Deploy web apps on AWS, Vercel, DigitalOcean with CI/CD pipeline support." },
              { icon: "🗂️", title: "Website Security", description: "Setup firewall, SSL, backup, malware scan & patching for WordPress or custom sites." }
            ].map((item, idx) => (
              <div key={idx} className="bg-white/10 rounded-lg text-center p-6 shadow border border-white/10">
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-white/70 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 z-50 bg-white text-black p-3 rounded-full shadow-xl hover:bg-slate-200"
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}

      <a
        href="https://wa.me/60167311882"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-20 right-6 z-50 bg-green-500 p-3 rounded-full shadow-xl hover:bg-green-600"
        aria-label="Chat on WhatsApp"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.72 13.06c-.27-.13-1.59-.79-1.83-.88-.24-.1-.42-.14-.6.13s-.7.88-.86 1.06c-.16.18-.32.2-.59.07-.27-.13-1.13-.42-2.15-1.34-.79-.7-1.32-1.57-1.47-1.84-.15-.27-.02-.41.11-.54.11-.11.27-.3.4-.44.13-.14.18-.24.27-.4.09-.16.05-.3-.02-.43-.07-.13-.6-1.45-.83-1.99-.22-.54-.45-.46-.6-.47-.16 0-.3-.01-.46-.01-.16 0-.42.06-.63.3-.21.24-.83.81-.83 2s.85 2.3.97 2.45c.12.15 1.67 2.55 4.06 3.57.57.25 1.01.4 1.36.52.57.18 1.09.15 1.5.09.46-.07 1.59-.65 1.82-1.28.23-.63.23-1.18.16-1.29-.07-.11-.25-.18-.52-.31z" />
        </svg>
      </a>

      <div className="py-12 text-center relative">
        <p className="typewriter text-white text-xl sm:text-2xl md:text-3xl font-semibold italic inline-block overflow-hidden border-r-2 border-white whitespace-nowrap mx-auto max-w-full sm:max-w-[34ch] leading-[2.5rem] md:leading-[3rem]">
          “Code the change you want to see.”
        </p>
      </div>

      <footer className="text-center text-white/60 mt-10">
        &copy; {new Date().getFullYear()} Cheang Hao Ying. All rights reserved.
      </footer>
    </div>
  );
}

export default App;